// valibot, not zod: this schema ships in the client bundle, where valibot's tree-shaking keeps it small.
import * as v from 'valibot'

import { FirestoreTimestampSchema } from '~shared/core/firestore'
import type { FirestoreDocumentSnapshot } from '~shared/core/firestore'
import { IsoDateSchema } from '~shared/timeutils'
import { FIRESTORE_ID_PATTERN, newFirestoreId } from '~shared/utils/newFirestoreId'

// =============== CANONICAL ENUMS
//
// Object-const style, not TypeScript `enum`. This is the canonical definition
// for the whole repo; `shared/types` no longer owns a Cue module.

export const CueType = {
  CUE: 'cue',
  HEADING: 'heading',
  GROUP: 'group',
} as const

export type CueType = (typeof CueType)[keyof typeof CueType]

export const CueStartMode = {
  FLEXIBLE: 'flexible',
  FIXED: 'fixed',
} as const

export type CueStartMode = (typeof CueStartMode)[keyof typeof CueStartMode]

export const SubTimerDirection = {
  DOWN: 'down',
  UP: 'up',
} as const

export type SubTimerDirection = (typeof SubTimerDirection)[keyof typeof SubTimerDirection]

/** What a countdown sub timer does when it reaches 00:00. */
export const SubTimerEndAction = {
  STOP: 'stop',
  CONTINUE: 'continue',
} as const

export type SubTimerEndAction = (typeof SubTimerEndAction)[keyof typeof SubTimerEndAction]

/** Furthest day after the show's start day a hard start can play on (`startDatePlus`). */
export const MAX_START_DATE_PLUS = 7

// =============== LEAF SCHEMAS

export const CueTypeSchema = v.picklist(Object.values(CueType))
export const CueStartModeSchema = v.picklist(Object.values(CueStartMode))

export const CueSettingsSchema = v.object({
  skipDuringShow: v.nullish(v.boolean(), false),
  hideOnPdf: v.nullish(v.boolean(), false),
  hideOnCsv: v.nullish(v.boolean(), false),
  preventEdits: v.nullish(v.boolean(), false),
})

export type CueSettings = v.InferOutput<typeof CueSettingsSchema>

export const SUB_TIMER_LIMITS = {
  PER_CUE: 20,
  NAME_LENGTH: 100,
  MAX_DURATION: 100 * 60 * 60 * 1000,
} as const

/**
 * A sub timer's settings. Running state lives on the runner, see
 * `sub-timer-run.ts`.
 */
export const SubTimerSchema = v.object({
  id: v.pipe(v.string(), v.regex(FIRESTORE_ID_PATTERN, 'Sub timer ids are 20-character Firestore-style ids')),
  name: v.nullish(v.pipe(v.string(), v.maxLength(SUB_TIMER_LIMITS.NAME_LENGTH)), ''),
  direction: v.nullish(v.picklist(Object.values(SubTimerDirection)), SubTimerDirection.DOWN),
  duration: v.nullish(v.pipe(v.number(), v.minValue(0), v.maxValue(SUB_TIMER_LIMITS.MAX_DURATION)), 0),
  endAction: v.nullish(v.picklist(Object.values(SubTimerEndAction)), SubTimerEndAction.STOP),
  autoStart: v.nullish(v.boolean(), false),
  /** Replaces the cue countdown on outputs. */
  showOnOutput: v.nullish(v.boolean(), false),
})

export type SubTimer = v.InferOutput<typeof SubTimerSchema>

// Written whole: the list is small and edited rarely, so a replace is simpler
// than per-timer paths.
const SubTimersSchema = v.pipe(v.array(SubTimerSchema), v.maxLength(SUB_TIMER_LIMITS.PER_CUE), v.readonly())

function createDefaultCueSettings(): CueSettings {
  return {
    skipDuringShow: false,
    hideOnPdf: false,
    hideOnCsv: false,
    preventEdits: false,
  }
}

// =============== FIRESTORE BOUNDARY
//
// The shape of `doc.data()` for a cue document. Timestamps in, `Date`s out.
// `id` and the audit stamps (`createdAt`/`updatedAt`) are handled by
// {@link parseCueSnapshot} from the snapshot envelope, not the document body.
// Every field is `nullish`-with-default so a legacy document that stores `null`
// (or omits the field) parses to a complete cue instead of throwing.

export const CueFirestoreSchema = v.object({
  type: v.nullish(CueTypeSchema, CueType.CUE),
  title: v.nullish(v.string(), ''),
  subtitle: v.nullish(v.string(), ''),
  startTime: v.nullish(FirestoreTimestampSchema, null),
  startMode: v.nullish(CueStartModeSchema, CueStartMode.FLEXIBLE),
  startDatePlus: v.nullish(v.number(), 0),
  duration: v.nullish(v.number(), 0),
  backgroundColor: v.nullish(v.string(), null),
  scheduled: v.nullish(v.boolean(), false),
  deletedAt: v.nullish(FirestoreTimestampSchema, null),
  settings: v.nullish(CueSettingsSchema, createDefaultCueSettings),
  subTimers: v.nullish(SubTimersSchema, () => []),
})

/** The raw Firestore document shape accepted by {@link CueFirestoreSchema}. */
export type CueFirestore = v.InferInput<typeof CueFirestoreSchema>

/** A parsed cue's own fields, before the snapshot envelope adds id/audit. */
export type CueFields = v.InferOutput<typeof CueFirestoreSchema>

// =============== WIRE BOUNDARY
//
// The over-the-wire shape: ISO date strings and the system fields included.
// Parses to the same canonical domain `Cue`.

export const CueSerializedSchema = v.object({
  id: v.string(),
  type: CueTypeSchema,
  title: v.string(),
  subtitle: v.string(),
  startTime: v.nullish(IsoDateSchema, null),
  startMode: CueStartModeSchema,
  startDatePlus: v.number(),
  duration: v.number(),
  backgroundColor: v.nullish(v.string(), null),
  scheduled: v.boolean(),
  deletedAt: v.nullish(IsoDateSchema, null),
  settings: CueSettingsSchema,
  subTimers: v.nullish(SubTimersSchema, () => []),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
})

/** The raw serialized shape accepted by {@link CueSerializedSchema}. */
export type CueSerialized = v.InferInput<typeof CueSerializedSchema>

// =============== WRITE BOUNDARY
//
// What a client may send to patch a cue: an allowlist of wire fields, so system
// fields stay server-owned and a new field is not writable until listed here.
// Strict, so an unknown key is rejected rather than written. Settings are patched
// one flag at a time through Firestore dot paths (`settings.preventEdits`), which
// is what the client sends; a nested `settings` object would replace the map.

export const CuePatchSchema = v.strictObject({
  ...v.partial(
    v.pick(CueSerializedSchema, [
      'type',
      'title',
      'subtitle',
      'startTime',
      'startMode',
      'startDatePlus',
      'duration',
      'backgroundColor',
      'scheduled',
      'subTimers',
    ]),
  ).entries,
  'settings.skipDuringShow': v.optional(v.boolean()),
  'settings.hideOnPdf': v.optional(v.boolean()),
  'settings.hideOnCsv': v.optional(v.boolean()),
  'settings.preventEdits': v.optional(v.boolean()),
})

export type CuePatch = v.InferOutput<typeof CuePatchSchema>

/** What a client may seed a new cue with. The rest comes from the defaults. */
export const CueCreateSchema = v.pick(CuePatchSchema, ['type', 'title', 'subtitle', 'duration', 'backgroundColor'])

export type CueCreate = v.InferOutput<typeof CueCreateSchema>

// =============== CANONICAL DOMAIN TYPE

/**
 * The canonical Cue.
 *
 * Fully parsed, `Date`-typed, with system fields.
 */
export type Cue = CueFields & {
  id: string
  createdAt: Date
  updatedAt: Date
}

// =============== PARSERS

// The whole document body, audit stamps included, so one parse reports every issue.
const CueDocumentSchema = v.object({
  ...CueFirestoreSchema.entries,
  createdAt: v.nullish(FirestoreTimestampSchema),
  updatedAt: v.nullish(FirestoreTimestampSchema),
})

/**
 * Parse a Firestore `DocumentSnapshot` into a canonical cue.
 *
 * The document body is validated by {@link CueFirestoreSchema}; `id` comes from
 * the snapshot and the audit stamps prefer an explicit document field, then the
 * snapshot metadata, then the epoch (`new Date(0)`, matching the server-side
 * `converters.fromSnapshot`).
 *
 * @throws {Error} naming the cue and every issue when the body does not match.
 */
export function parseCueSnapshot(snapshot: FirestoreDocumentSnapshot): Cue {
  const result = v.safeParse(CueDocumentSchema, snapshot.data() ?? {})
  if (!result.success) throw new Error(`Invalid cue document ${snapshot.id}:\n${v.summarize(result.issues)}`)
  const { createdAt, updatedAt, ...fields } = result.output

  return {
    ...fields,
    id: snapshot.id,
    createdAt: createdAt ?? snapshot.createTime?.toDate() ?? new Date(0),
    updatedAt: updatedAt ?? snapshot.updateTime?.toDate() ?? new Date(0),
  }
}

/**
 * Parse a serialized cue (ISO date strings) into a canonical cue.
 *
 * @throws {ValiError} when the input does not match.
 */
export function parseCueSerialized(input: unknown): Cue {
  return v.parse(CueSerializedSchema, input)
}

/**
 * Serialize a canonical cue into the over-the-wire shape.
 *
 * `Date` does not survive JSON, so every date field is emitted as an ISO string
 * (or `null`). The result round-trips through {@link parseCueSerialized}.
 */
export function serializeCue(cue: Cue): CueSerialized {
  return {
    ...cue,
    startTime: cue.startTime?.toISOString() ?? null,
    deletedAt: cue.deletedAt?.toISOString() ?? null,
    createdAt: cue.createdAt.toISOString(),
    updatedAt: cue.updatedAt.toISOString(),
    subTimers: [...cue.subTimers],
  }
}

/**
 * A copy of a cue's document data with a fresh id for every sub timer.
 *
 * Use whenever a cue is copied (duplicate, rundown clone, templates). Running
 * state is keyed by sub timer id, so a copy that kept the ids would start and
 * stop together with the original.
 */
export function withNewSubTimerIds<T extends Pick<CueFirestore, 'subTimers'>>(
  cue: T,
  newId: () => string = newFirestoreId,
): T {
  if (!cue.subTimers?.length) return cue
  return { ...cue, subTimers: cue.subTimers.map((timer) => ({ ...timer, id: newId() })) }
}

/**
 * The schema's default cue fields. The single source of defaults.
 */
export function getCueSchemaDefaults(): CueFields {
  return v.getDefaults(CueFirestoreSchema)
}

/** Branded id of {@link Cue}. */
export type CueId = Cue['id'] & { readonly __brand: 'CueId' }
